import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';
import { Client } from '../clients/useData';

// Marketing uses the same Client interface but filtered for marketing (isClient: false)
export type MarketingClient = Client & {
  // Additional fields from backend includes
  clientCountryDescr?: {
    pc_descr?: string;
  };
  clientTaxAuthorityDescr?: {
    pt_descr?: string;
  };
  clientCalls?: Array<{
    clientCallDate?: string | null;
  }>;
  clientAppointments?: Array<{
    clientAppScheduleDateTime?: string | null;
  }>;
  _count?: {
    clientCalls?: number;
    clientReminders?: number;
    clientAppointments?: number;
    FileAttachment?: number;
  };
};

export interface MarketingResponse {
  records: MarketingClient[];
  withoutCallsCount?: number;
}

export const useMarketing = (searchParams?: string, filter?: string) => {
  let queryString = '';
  if (searchParams || filter) {
    const params = new URLSearchParams();
    if (searchParams) params.append('searchParams', searchParams);
    if (filter) params.append('filter', filter);
    queryString = `?${params.toString()}`;
  }
  
  const { data, error, isLoading, mutate } = useSWR<MarketingResponse>(
    `${moduleConfig.module.api.list}${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  // Extract records array from response
  const clients = data?.records || [];

  return {
    data: clients,
    clients: clients,
    total: clients.length,
    isLoading,
    isError: error,
    mutate,
    withoutCallsCount: data?.withoutCallsCount || 0,
  };
};

export const useMarketingClient = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<MarketingClient>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    client: data,
    data: data,
    isLoading,
    isError: error,
    mutate,
  };
};

// Hook to get countries for select dropdown
export const useCountriesForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/p-country',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    countries: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((country) => ({
      value: String(country.id),
      label: country.pc_descr || `Country #${country.id}`,
    })),
  };
};

// Hook to get tax authorities for select dropdown
export const useTaxAuthoritiesForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/p-tax-authority',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    taxAuthorities: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((tax) => ({
      value: String(tax.id),
      label: tax.pt_descr || `Tax Authority #${tax.id}`,
    })),
  };
};

